import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCalendarCheck, FaClock, FaTimes } from 'react-icons/fa';
import BookingForm from './BookingForm';
import { useBooking } from '../../contexts/BookingContext';
import { useSiteInfo } from '../../contexts/SiteInfoContext';
import { normalizePhoneNumbers, getAddressLines } from '../../utils/businessInfo';
import './BookingModal.css';

const BookingModal = () => {
  const { isModalOpen, closeBookingModal } = useBooking();
  const { siteInfo } = useSiteInfo();

  const hours = siteInfo?.hours ?? {};
  const company = siteInfo?.company ?? {};
  const businessInfo = siteInfo?.business_info ?? {};
  const phoneNumbers = useMemo(
    () => normalizePhoneNumbers(businessInfo, company),
    [businessInfo, company]
  );
  const addressLines = useMemo(
    () => getAddressLines(businessInfo, company),
    [businessInfo, company]
  );
  const fallbackAddress = company.address || '2A Supertron Court, Laverton North, VIC 3026';

  const formattedHours = Object.entries(hours).map(([day, data]) => {
    if (data.closed) {
      return { day, label: 'Closed' };
    }
    return { day, label: `${data.open} - ${data.close}` };
  });

  const handleOverlayClick = () => {
    closeBookingModal();
  };

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          className="booking-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
        >
          <motion.div
            className="booking-modal"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.25 }}
            onClick={stopPropagation}
          >
            <button className="booking-modal-close" onClick={closeBookingModal} aria-label="Close booking form">
              <FaTimes />
            </button>
            <div className="booking-modal-grid">
              <div className="booking-modal-intro">
                <span className="booking-modal-badge">
                  <FaCalendarCheck /> Book Your Appointment
                </span>
                <h2>Keeping Your Ride Road-Ready</h2>
                <p>
                  Reserve a time that works for you and our technicians will take care of the rest. We&apos;ll confirm
                  availability and finalise details shortly after you submit.
                </p>

                <div className="booking-modal-info">
                  <div className="info-block">
                    <h3>Workshop</h3>
                    <p>{addressLines.length > 0 ? addressLines.join(', ') : fallbackAddress}</p>
                    {phoneNumbers.length > 0 && (
                      <p className="info-phone">{phoneNumbers.map(p => p.number).join(' • ')}</p>
                    )}
                  </div>

                  <div className="info-block">
                    <h3>
                      <FaClock /> Trading Hours
                    </h3>
                    <ul>
                      {formattedHours.map((item) => (
                        <li key={item.day}>
                          <span className="day">{item.day.charAt(0).toUpperCase() + item.day.slice(1)}</span>
                          <span className="hour">{item.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="booking-modal-form">
                <BookingForm onSuccess={closeBookingModal} />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
